import {Group, Text, useMantineTheme, MantineTheme, Stack, Divider} from '@mantine/core';
import {Upload, X, Icon as TablerIcon, Files, Download} from 'tabler-icons-react';
import {Dropzone, DropzoneProps, DropzoneStatus} from '@mantine/dropzone';

function getIconColor(status: DropzoneStatus, theme: MantineTheme) {
    return status.accepted
        ? theme.colors[theme.primaryColor][theme.colorScheme === 'dark' ? 4 : 6]
        : status.rejected
            ? theme.colors.red[theme.colorScheme === 'dark' ? 4 : 6]
            : theme.colorScheme === 'dark'
                ? theme.colors.dark[0]
                : theme.colors.gray[7];
}

function ImageUploadIcon({status, ...props}: React.ComponentProps<TablerIcon> & { status: DropzoneStatus }) {
    if (status.accepted) {
        return <Upload {...props} />;
    }

    if (status.rejected) {
        return <X {...props} />;
    }

    return <Files {...props} />;
}

export const dropzoneChildren = (status: DropzoneStatus, theme: MantineTheme, props: MantineDropzoneProps) => {
    function getAcceptedFiles() {
        if (props.accept) {
            return props.accept.join(", ");
        } else {
            return "All"
        }
    }

    function getMbSize() {
        if (props.maxSize) {
            return `Each file should not exceed ${props.maxSize / (1024 * 1024)}mb`
        } else {
            return "No max size";
        }
    }

    if (props.customRenderFiles) {
        if(props.files) return props.customRenderFiles(props?.files);
    }

    else if (props.files && props.files.length !== 0) {
        return (
            <Stack>
                {props.files.map((file, index) => {
                    return (
                        <Stack key={index}>
                            <Group position="apart">
                                <Text size="sm">{file.name}</Text>
                                <Group>
                                    <X onClick={(e) => {
                                        e.stopPropagation();
                                        props.onDeleteFile(index)
                                    }}/>
                                </Group>
                            </Group>
                            {index !== props.files!.length - 1 &&
                                <Divider/>}
                        </Stack>
                    )
                })}
            </Stack>
        )
    }

    return (
        <Group position="center" spacing="xl" align="center"
               style={{flexWrap: "nowrap", pointerEvents: 'none', padding: 20}}>
            <ImageUploadIcon status={status} style={{color: getIconColor(status, theme)}} size={80}/>
            <Stack>
                <Text size="xl" inline>
                    Drag files here or click to select files
                </Text>
                <Text size="sm" color="dimmed" inline mt={7}>
                    Attach as many files as you like. {getMbSize()}. Accepted
                    files: {getAcceptedFiles()}
                </Text>
            </Stack>
        </Group>
    )
};

interface MantineDropzoneProps extends Omit<DropzoneProps, "children"> {
    files?: File[],
    onDeleteFile: (index: number) => void,
    customRenderFiles?: any,
}

function MantineDropzone(props: MantineDropzoneProps) {
    const theme = useMantineTheme();
    const {onDeleteFile, customRenderFiles, ...rest} = props;
    return (
        <Dropzone {...rest}>
            {(status) => dropzoneChildren(status, theme, props)}
        </Dropzone>
    );
}

export default MantineDropzone;